import { padAgendaCodTec } from '../services/scheduleService';
import { preventivaToTipoLetter } from '../../board/board-search-filters/tipoOsFilterUtils';
import { normalizeHexColor } from '../../board/utils/defeitoAlertBadge';

const TYPE_COLORS = {
  E: '#9c27b0',
  B: '#ff5722',
  A: '#607d8b',
  R: '#795548',
  D: '#f44336',
  I: '#2196f3',
  S: '#4caf50',
  C: '#ff9800',
};

/** Cores ativas da paleta (borda, badge, ponto do técnico). */
const ACTIVE_PALETTE = [
  '#7C4DFF',
  '#0D9B8A',
  '#FF6A3D',
  '#C2185B',
  '#1B7A4A',
  '#E0A106',
  '#2B5CFF',
  '#A56B72',
  '#12B5C9',
  '#8A7460',
];

const ACTIVE_HUES = [265, 174, 16, 338, 148, 44, 226, 350, 188, 28];

function hslToHex(h, s, l) {
  const sat = s / 100;
  const light = l / 100;
  const a = sat * Math.min(light, 1 - light);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const c = light - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * c).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function activeAt(index) {
  const i = Math.max(0, Number(index) || 0);
  if (i < ACTIVE_PALETTE.length) return ACTIVE_PALETTE[i];
  const extra = i - ACTIVE_PALETTE.length;
  const hue = (ACTIVE_HUES[extra % ACTIVE_HUES.length] + Math.floor(extra / ACTIVE_HUES.length) * 16) % 360;
  const sat = 68 - (Math.floor(extra / ACTIVE_HUES.length) % 3) * 6;
  const light = 44 + (extra % 2) * 4;
  return hslToHex(hue, sat, light);
}

export function buildTechColorMap(ids) {
  const uniq = [];
  const seen = new Set();
  (ids || []).forEach((id) => {
    const padded = padAgendaCodTec(id);
    if (!padded || seen.has(padded)) return;
    seen.add(padded);
    uniq.push(padded);
  });
  uniq.sort();
  const map = {};
  uniq.forEach((id, i) => {
    const color = activeAt(i);
    const stripped = String(id).replace(/\D/g, '').replace(/^0+/, '') || id;
    map[id] = color;
    map[stripped] = color;
  });
  return map;
}

export function colorForTech(id, colorMap) {
  if (colorMap) {
    const padded = padAgendaCodTec(id);
    const stripped = String(id == null ? '' : id).replace(/\D/g, '').replace(/^0+/, '') || padded;
    return colorMap[padded] || colorMap[stripped] || colorMap[String(id || '').trim()] || ACTIVE_PALETTE[0];
  }
  const padded = padAgendaCodTec(id);
  let h = 0;
  const s = String(padded || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return activeAt(h % (ACTIVE_PALETTE.length * 3));
}

export function osServiceBadge(order) {
  const alert = String((order && (order.defeitoAlert || order.defeito_alert)) || '').trim();
  if (alert) {
    return {
      text: alert,
      bg: normalizeHexColor(order.colorBg || order.color_bg) || '#8458fe',
      alert: true,
    };
  }
  const letter = preventivaToTipoLetter((order && (order.preventiva || order.TB02115_PREVENTIVA)) || 'N');
  return { text: letter, bg: TYPE_COLORS[letter] || '#9e9e9e', alert: false };
}

function esc(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[c]));
}

function shortLabel(name) {
  const s = String(name || '').trim();
  if (!s) return '—';
  if (s.length <= 22) return s;
  return `${s.slice(0, 20)}…`;
}

export function clusterRouteHelpPoints(points, colorMap) {
  const map = {};
  (points || []).forEach((p) => {
    if (!p || !Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return;
    const lat = Number(p.lat);
    const lng = Number(p.lng);
    const codTecNorm = padAgendaCodTec(p.codTec || '') || '';
    const key = `${lat.toFixed(5)}|${lng.toFixed(5)}|${codTecNorm}`;
    if (!map[key]) {
      map[key] = {
        key,
        lat,
        lng,
        codTec: codTecNorm,
        color: colorForTech(codTecNorm, colorMap),
        bairro: String(p.bairro || '').trim(),
        orders: [],
      };
    }
    map[key].orders.push({
      os: String(p.os || '').trim(),
      cliente: String(p.cliente || '').trim(),
      bairro: String(p.bairro || '').trim(),
      defeitoAlert: String(p.defeitoAlert || '').trim(),
      colorBg: String(p.colorBg || '').trim(),
      preventiva: String(p.preventiva || '').trim(),
    });
    if (!map[key].bairro && p.bairro) map[key].bairro = String(p.bairro).trim();
  });
  const clusters = Object.keys(map).map((k) => map[k]);
  clusters.forEach((c) => {
    c.count = c.orders.filter((o) => o.os).length;
  });
  return clusters;
}

/** Mesma pane (overlayMouseTarget): z-index CSS vale. Não usar floatPane — cobriria o pin de origem. */
export const ROUTE_PIN_Z = {
  os: '12',
  origin: '40',
  osHover: '50',
  osFront: '60',
  originFront: '80',
};

export function createPinOverlay(googleMaps, { lat, lng, el, onClick, zIndex }) {
  class PinOverlay extends googleMaps.OverlayView {
    constructor() {
      super();
      this.position = new googleMaps.LatLng(lat, lng);
      this.el = el;
      this.onClick = onClick;
      this.zIndex = zIndex != null ? String(zIndex) : ROUTE_PIN_Z.os;
      this._attached = false;
    }

    onAdd() {
      if (!this.el) return;
      this.el.style.position = 'absolute';
      if (!this.el.style.zIndex) this.el.style.zIndex = this.zIndex;
      const panes = this.getPanes();
      if (!panes || !panes.overlayMouseTarget) return;
      panes.overlayMouseTarget.appendChild(this.el);
      this._click = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.onClick) this.onClick(e);
      };
      this.el.addEventListener('click', this._click);
      this._attached = true;
    }

    draw() {
      const projection = this.getProjection();
      if (!projection || !this.el) return;
      const point = projection.fromLatLngToDivPixel(this.position);
      if (!point) return;
      this.el.style.left = `${point.x}px`;
      this.el.style.top = `${point.y}px`;
      this.el.style.transform = 'translate(-50%, -100%)';
    }

    setPosition(nextLat, nextLng) {
      this.position = new googleMaps.LatLng(nextLat, nextLng);
      if (this._attached && this.el) this.draw();
    }

    onRemove() {
      this._attached = false;
      const node = this.el;
      if (node && this._click) {
        try {
          node.removeEventListener('click', this._click);
        } catch (_) {
          /* ignore */
        }
      }
      this._click = null;
      if (node && node.parentNode) {
        try {
          node.parentNode.removeChild(node);
        } catch (_) {
          /* ignore */
        }
      }
      // Mantém this.el para não quebrar onAdd atrasado; overlay removido não é reutilizado.
    }
  }
  return new PinOverlay();
}

export function isClientPointOnEl(clientX, clientY, el) {
  if (!el || !Number.isFinite(clientX) || !Number.isFinite(clientY)) return false;
  const r = el.getBoundingClientRect();
  return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
}

/** Sobe o pin na mesma pane (append + z-index), sem floatPane. */
export function bringRouteHelpPinToFront(el) {
  if (!el) return;
  el.dataset.front = '1';
  el.style.zIndex = ROUTE_PIN_Z.osFront;
  if (el.parentNode) el.parentNode.appendChild(el);
}

export function clearRouteHelpPinFront(el) {
  if (!el) return;
  el.dataset.front = '0';
  el.style.zIndex = ROUTE_PIN_Z.os;
}

export function bringOriginPinToFront(el) {
  if (!el) return;
  el.dataset.front = '1';
  el.style.zIndex = ROUTE_PIN_Z.originFront;
  if (el.parentNode) el.parentNode.appendChild(el);
}

export function clearOriginPinFront(el) {
  if (!el) return;
  el.dataset.front = '0';
  el.style.zIndex = ROUTE_PIN_Z.origin;
}

export function buildRouteHelpPinEl(cluster) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'oosv2-agenda-pin';
  btn.style.setProperty('--pin-color', cluster.color || '#7C4DFF');
  btn.title = 'Este atendimento';
  btn.dataset.front = '0';
  btn.style.zIndex = ROUTE_PIN_Z.os;
  const n = cluster.count || (cluster.orders || []).length || 1;
  const bairro = shortLabel(cluster.bairro || '—');
  btn.setAttribute('aria-label', `${n} OS · ${bairro}`);

  btn.innerHTML =
    `<span class="oosv2-agenda-pin-bubble">` +
    `<span class="oosv2-agenda-pin-badge">${esc(String(n))}</span>` +
    `<span class="oosv2-agenda-pin-label">${esc(bairro)}</span>` +
    `</span>` +
    `<span class="oosv2-agenda-pin-stem"></span>` +
    `<span class="oosv2-agenda-pin-dot"></span>`;

  const bump = () => {
    if (btn.dataset.front === '1') return;
    btn.style.zIndex = ROUTE_PIN_Z.osHover;
  };
  const restore = () => {
    if (btn.dataset.front === '1') return;
    btn.style.zIndex = ROUTE_PIN_Z.os;
  };
  btn.addEventListener('pointerenter', bump);
  btn.addEventListener('pointerleave', restore);
  btn.addEventListener('focus', bump);
  btn.addEventListener('blur', restore);

  return btn;
}

export function buildOriginPinEl(title) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'oosv2-agenda-origin-pin';
  btn.dataset.front = '0';
  btn.style.zIndex = ROUTE_PIN_Z.origin;
  const tip = String(title || '').trim() || 'Atendimento a abrir';
  btn.title = tip;
  btn.setAttribute('aria-label', tip);
  btn.innerHTML =
    '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44" aria-hidden="true">' +
    '<path fill="#6d3ef5" d="M18 0C8.6 0 1 7.6 1 17c0 12.4 17 27 17 27s17-14.6 17-27C35 7.6 27.4 0 18 0z"/>' +
    '<circle cx="18" cy="16.2" r="7.2" fill="#fff"/>' +
    '<path fill="#6d3ef5" d="M18 11.2l1.5 3.1 3.4.5-2.45 2.4.6 3.4L18 18.9l-3.05 1.7.6-3.4-2.45-2.4 3.4-.5z"/>' +
    '</svg>';
  return btn;
}

const MONITORED_STATUS_COLORS = {
  em_servico: '#34d399',
  deslocamento: '#60a5fa',
  ocioso: '#fbbf24',
  almoco: '#f43f5e',
  nao_alocado: '#a1a1aa',
};

function shortMonitoredTechName(nome) {
  const s = String(nome || '').trim();
  if (!s) return 'Técnico';
  if (s.length <= 18) return s;
  return `${s.slice(0, 16)}…`;
}

export function buildAgendaMonitoredTechPinEl(tech) {
  const wrap = document.createElement('div');
  wrap.className = 'oosv2-agenda-tech-marker';
  const status = String(tech?.status || 'nao_alocado').trim();
  const color = MONITORED_STATUS_COLORS[status] || MONITORED_STATUS_COLORS.nao_alocado;
  wrap.style.setProperty('--tech-status', color);
  wrap.setAttribute('data-status', status);

  const label = document.createElement('span');
  label.className = 'oosv2-agenda-tech-marker-label';
  const nameChip = document.createElement('span');
  nameChip.className = 'oosv2-agenda-tech-marker-name';
  const nome = shortMonitoredTechName(tech?.nome);
  nameChip.textContent = nome;
  if (tech?.nome) nameChip.title = String(tech.nome);
  label.appendChild(nameChip);
  if (status === 'deslocamento' && Number.isFinite(Number(tech?.speedKmh))) {
    const speedChip = document.createElement('span');
    speedChip.className = 'oosv2-agenda-tech-marker-speed';
    speedChip.textContent = `${Math.round(Number(tech.speedKmh))} km/h`;
    label.appendChild(speedChip);
  }
  wrap.appendChild(label);

  const dot = document.createElement('span');
  dot.className = 'oosv2-agenda-tech-marker-dot';
  dot.textContent = String(tech?.iniciais || nome.charAt(0) || '•').slice(0, 2).toUpperCase();
  wrap.appendChild(dot);
  return wrap;
}
